import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { resetAccountPassword, type AccountPasswordReset } from '@/api/console';
import { OrgAdminApiError, revokeOrgAssignment, type ScopedAccountRow } from '@/api/orgAdmin';
import type { ScopedAccountsVM } from './useScopedAccounts';

/**
 * EAA-1839: the single-row writes of the scoped accounts browser — everything the
 * "⋯" menu and the "+ Tài khoản" button do.
 *
 * WHAT IS DELIBERATELY ABSENT, and why. Frame 34's bulk bar offers four actions;
 * only ONE of them has a bulk endpoint. `authoring_views.py` ships exactly
 * `assignments/bulk-preview` and `assignments/bulk-apply` (Level assign). There is
 * no bulk change-đơn-vị, no bulk password reset and no bulk deactivate anywhere in
 * portal-api — and this ticket's own scope note puts "Đổi đơn vị hàng loạt" in a
 * separate ticket. Those three bulk buttons are therefore rendered DISABLED with
 * the real reason (see `ScopedAccountsBulkBar`), never looped over a single-row
 * endpoint behind a bulk-looking label.
 *
 * Two single-row actions ARE real and are exposed per row instead:
 *   - "Thu hồi quyền" → `DELETE /organizations/{id}/assignments/{assignmentId}`,
 *     a soft revoke (the row stays, `is_active` flips, the revoke is pushed).
 *   - "Đặt lại mật khẩu" → `POST /accounts/reset-password`, which is
 *     `IsSuperAdmin` server-side, so it is gated on `console.accounts.reset` and
 *     simply absent for an org-admin.
 *
 * EAA-1839 (frame 35 completion pass): the "+ Tài khoản" grant flow that used to
 * live here moved to `useProvisionAccount` (frames 35e/35f) — see
 * `ScopedAccountDialogs`'s docstring for why the typed-`user_sub` form was replaced
 * rather than kept alongside a searchable one. This hook is now exactly the two
 * per-row writes.
 *
 * There is still no way to disable a Keycloak account from this screen and no bulk
 * way to move an account between đơn vị — the single-row move is frame 85's
 * "Chuyển đơn vị" (`useRowTransfer`), which is its own hook.
 */
export function useScopedAccountWrites(list: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const { invalidateList } = list;

    const fail = useCallback(
        (fallback: string) => (error: unknown) =>
            toast.error(error instanceof OrgAdminApiError ? error.message : fallback),
        [toast]
    );

    /* ------------------------------------------------------- revoke a grant */

    const [revokeTarget, setRevokeTarget] = useState<ScopedAccountRow | null>(null);

    const revokeMutation = useMutation({
        mutationFn: (row: ScopedAccountRow) =>
            // EAA-1839: the URL org is the ROW's own organization, not the scope
            // org — with "Gồm đơn vị con" on, a row can belong to a descendant
            // đơn vị, and `OrganizationAssignmentDetailView` resolves the
            // assignment inside the org named in the path.
            revokeOrgAssignment(config.apiBase, accessToken as string, row.organization_id, row.id),
        onSuccess: (_data, row) => {
            setRevokeTarget(null);
            invalidateList();
            toast.success(
                t('console.hierarchy.scopedAccounts.writes.revokeSuccess', {
                    identity: row.user_email || row.user_sub,
                })
            );
        },
        onError: fail(t('console.hierarchy.scopedAccounts.writes.revokeErrorFallback')),
    });

    /* ---------------------------------------------------- reset a password */

    const [resetTarget, setResetTarget] = useState<ScopedAccountRow | null>(null);
    const [resetResult, setResetResult] = useState<AccountPasswordReset | null>(null);

    const resetMutation = useMutation({
        mutationFn: (row: ScopedAccountRow) =>
            resetAccountPassword(config.apiBase, accessToken as string, { sub: row.user_sub }),
        onSuccess: (data) => {
            setResetTarget(null);
            setResetResult(data);
            toast.success(
                t('console.hierarchy.scopedAccounts.writes.resetSuccess', {
                    username: data.username,
                })
            );
        },
        onError: fail(t('console.hierarchy.scopedAccounts.writes.resetErrorFallback')),
    });

    return {
        revokeTarget,
        setRevokeTarget,
        runRevoke: revokeMutation.mutate,
        revokeBusy: revokeMutation.isPending,

        resetTarget,
        setResetTarget,
        runReset: resetMutation.mutate,
        resetBusy: resetMutation.isPending,
        resetResult,
        dismissResetResult: () => setResetResult(null),
    };
}

export type ScopedAccountWritesVM = ReturnType<typeof useScopedAccountWrites>;
